/**
 * Camera Tab - Configure camera settings for timelapse capture
 * Phase 4: Advanced Camera Controls
 *
 * Settings are pushed to Android device (ARCH-001: Dashboard is King)
 * Only shows options available on the connected device
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form,
  Switch,
  Select,
  Slider,
  Segmented,
  Typography,
  Button,
  message,
  Alert,
  Modal,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { View, CameraSettings, CameraConfig } from '@/types';
import { MaterialIcon } from '@/components/MaterialIcon';
import { CameraImage } from '@/components/CameraImage';
import { DslrControls } from '@/components/DslrControls';
import { requestTestShot, updateDslrDesiredProperty, updateTimerSettings } from '@/services/viewService';
import { SectionLabel } from '@/components/SectionLabel';
import { FieldLabel } from '@/components/FieldLabel';
import { HelperText } from '@/components/HelperText';
import { GatedControl } from '@/components/TierBadge';
import { useViewEntitlements } from '@/hooks/useViewEntitlements';
import {
  SECTION_GAP,
  BORDER,
  RADIUS,
  SECONDARY,
  SURFACE,
  TEXT_STRONG,
  WARNING_SOFT,
} from '@/styles/design-tokens';


const { Text } = Typography;
const { Option } = Select;

interface CameraTabProps {
  view: View;
  viewId?: string;
  onSave: (cameraSettings: CameraSettings) => Promise<void>;
  hideTestShotButton?: boolean;
}

// Default camera settings
const DEFAULT_SETTINGS: CameraSettings = {
  lens: 'wide',
  focusMode: 'manual',  // Manual focus (infinity by default) prevents focus flickering in timelapse
  manualFocusDistance: 0,  // 0 = infinity, in diopters (1/meters)
  evCompensation: 0,
  hdrMode: 'off',
  jpegQuality: 'good',
  rawEnabled: false,
  gpsEnabled: false,
  // Phase 4.1: Low Noise Mode defaults
  lnmEnabled: true,
  lnmThreshold: 2.0,
  maxShutterSeconds: 1.0,
};

// Focus distance conversion utilities
// Camera2 API uses diopters (1/meters): 0 = infinity, higher = closer
function formatFocusDistance(diopters: number): string {
  if (diopters === 0 || diopters < 0.01) return '∞';
  const meters = 1 / diopters;
  if (meters >= 100) return '∞';
  if (meters >= 10) return `${Math.round(meters)}m`;
  if (meters >= 1) return `${meters.toFixed(1)}m`;
  return `${Math.round(meters * 100)}cm`;
}

function formatMinDistance(diopters: number): string {
  if (diopters === 0 || diopters < 0.01) return '∞';
  const meters = 1 / diopters;
  if (meters >= 1) return `${meters.toFixed(1)}m`;
  return `${Math.round(meters * 100)}cm`;
}

// Convert diopters to slider percent (0% = closest, 100% = infinity)
function focusDistanceToPercent(diopters: number, maxDiopters: number): number {
  if (maxDiopters <= 0) return 100;
  return 100 - (diopters / maxDiopters * 100);
}

// Convert slider percent to diopters (100% = 0 diopters = infinity)
function percentToFocusDistance(percent: number, maxDiopters: number): number {
  return maxDiopters * (1 - percent / 100);
}

// JPEG Quality labels
const JPEG_QUALITY_OPTIONS = [
  { label: 'Medium (70%)', value: 'medium' as const },
  { label: 'Good (85%)', value: 'good' as const },
  { label: 'Max (95%)', value: 'max' as const },
];

// EV compensation marks for slider
const EV_MARKS = {
  '-2': '-2',
  '-1': '-1',
  '0': '0',
  '1': '+1',
  '2': '+2',
};

// Debounce delay for slider controls (EV, focus distance)
const DEBOUNCE_DELAY = 500;

export function CameraTab({ view, viewId, onSave, hideTestShotButton }: CameraTabProps) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isShooting, setIsShooting] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const config = view.settings?.camera;
  const cameraMode = config?.mode ?? 'internal';
  const savedCamera = config?.internal;
  const savedDslrSettings = config?.dslr ?? { rawEnabled: false };
  const isDslrMode = cameraMode === 'dslr';

  // Initialize state directly from view to avoid flash of default values
  const [lens, setLens] = useState<CameraSettings['lens']>(savedCamera?.lens ?? DEFAULT_SETTINGS.lens);
  const [focusMode, setFocusMode] = useState<CameraSettings['focusMode']>(savedCamera?.focusMode ?? DEFAULT_SETTINGS.focusMode);
  const [manualFocusDistance, setManualFocusDistance] = useState<number>(savedCamera?.manualFocusDistance ?? DEFAULT_SETTINGS.manualFocusDistance ?? 0);
  const [evCompensation, setEvCompensation] = useState<number>(savedCamera?.evCompensation ?? DEFAULT_SETTINGS.evCompensation);
  const [jpegQuality, setJpegQuality] = useState<CameraSettings['jpegQuality']>(savedCamera?.jpegQuality ?? DEFAULT_SETTINGS.jpegQuality);
  const [hdrMode, setHdrMode] = useState<CameraSettings['hdrMode']>(savedCamera?.hdrMode ?? DEFAULT_SETTINGS.hdrMode);
  const [rawEnabled, setRawEnabled] = useState<boolean>(savedCamera?.rawEnabled ?? DEFAULT_SETTINGS.rawEnabled);
  const [lnrMode, setLnrMode] = useState<NonNullable<CameraSettings['lnrMode']>>(savedCamera?.lnrMode ?? 'off');
  const [dslrRawEnabled, setDslrRawEnabled] = useState<boolean>(savedDslrSettings.rawEnabled);
  const [dslrRefreshing, setDslrRefreshing] = useState(false);

  // Get camera capabilities from Android status
  const cameras = view.androidStatus?.device?.cameras || [];
  const hasMultipleCameras = cameras.length > 1;

  // Get capabilities for selected lens
  const selectedCamera = cameras.find(c => c.lens === lens) || cameras[0];
  const supportsRaw = selectedCamera?.supportsRaw || false;
  // RAW multiplies file size, so it belongs to the tiers with room for it.
  // Hardware support and the plan are separate gates: the control only
  // appears when the device can do RAW, and is switched off when the plan
  // does not grant it.
  const { get: getEntitlements } = useViewEntitlements();
  const rawAllowed = getEntitlements(view.viewId).raw;
  const supportsHdr = (selectedCamera?.hdrModes?.length ?? 0) > 1; // has more than just "off"
  const isHdrOn = hdrMode !== 'off';
  const evRange = selectedCamera?.evRange || { min: -2, max: 2, step: 0.5 };
  const minFocusDistance = selectedCamera?.minFocusDistance || 0;  // in diopters

  // Extract view.settings.camera to avoid re-triggering on androidStatus changes
  const viewCameraSettings = view.settings?.camera;

  // Sync state from Firebase (real-time updates from Android/viewfinder)
  useEffect(() => {
    const liveConfig = viewCameraSettings;
    const internal = liveConfig?.internal;

    if (internal?.lens !== undefined) setLens(internal.lens);
    if (internal?.focusMode !== undefined) setFocusMode(internal.focusMode);
    if (internal?.manualFocusDistance !== undefined) setManualFocusDistance(internal.manualFocusDistance);
    if (internal?.evCompensation !== undefined) setEvCompensation(internal.evCompensation);
    if (internal?.jpegQuality !== undefined) setJpegQuality(internal.jpegQuality);
    if (internal?.hdrMode !== undefined) setHdrMode(internal.hdrMode);
    if (internal?.rawEnabled !== undefined) setRawEnabled(internal.rawEnabled);
    if (internal?.lnrMode !== undefined) setLnrMode(internal.lnrMode);

    setDslrRawEnabled(liveConfig?.dslr?.rawEnabled ?? false);
  }, [viewCameraSettings]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Save settings to Firebase (silent, no success message)
  const saveSettings = useCallback(async (overrides: Partial<CameraSettings & { dslrRawEnabled?: boolean }> = {}) => {
    try {
      const existing = view.settings?.camera;

      const internalSettings: CameraSettings = {
        lens: overrides.lens ?? lens ?? DEFAULT_SETTINGS.lens,
        focusMode: overrides.focusMode ?? focusMode ?? DEFAULT_SETTINGS.focusMode,
        manualFocusDistance: overrides.manualFocusDistance ?? manualFocusDistance ?? 0,
        evCompensation: overrides.evCompensation ?? evCompensation ?? DEFAULT_SETTINGS.evCompensation,
        hdrMode: overrides.hdrMode ?? hdrMode ?? DEFAULT_SETTINGS.hdrMode,
        jpegQuality: overrides.jpegQuality ?? jpegQuality ?? DEFAULT_SETTINGS.jpegQuality,
        rawEnabled: (overrides.rawEnabled ?? rawEnabled ?? false) && supportsRaw,
        gpsEnabled: existing?.internal?.gpsEnabled ?? DEFAULT_SETTINGS.gpsEnabled,
        lnrMode: overrides.lnrMode ?? lnrMode ?? 'off',
        lnmEnabled: existing?.internal?.lnmEnabled ?? DEFAULT_SETTINGS.lnmEnabled,
        lnmThreshold: existing?.internal?.lnmThreshold ?? DEFAULT_SETTINGS.lnmThreshold,
        maxShutterSeconds: existing?.internal?.maxShutterSeconds ?? DEFAULT_SETTINGS.maxShutterSeconds,
      };

      const newDslrRawEnabled = overrides.dslrRawEnabled ?? dslrRawEnabled;

      const newConfig: CameraConfig = isDslrMode
        ? { mode: 'dslr', internal: existing?.internal ?? internalSettings, dslr: { rawEnabled: newDslrRawEnabled } }
        : { mode: 'internal', internal: internalSettings, dslr: existing?.dslr ?? { rawEnabled: false } };

      await onSave(newConfig as unknown as CameraSettings);
    } catch (error) {
      message.error('Failed to save camera settings');
    }
  }, [lens, focusMode, manualFocusDistance, evCompensation, hdrMode, jpegQuality, rawEnabled, lnrMode, supportsRaw, onSave, view.settings, dslrRawEnabled, isDslrMode]);

  // Debounced save for slider controls
  const debouncedSave = useCallback((overrides: Partial<CameraSettings> = {}) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveSettings(overrides);
    }, DEBOUNCE_DELAY);
  }, [saveSettings]);

  // Test shot handler
  const handleTestShot = useCallback(async () => {
    setIsShooting(true);
    try {
      await requestTestShot(view.userId, view.projectId, view.viewId);
      message.success('Test shot requested');
    } catch (error) {
      message.error('Failed to request test shot');
    } finally {
      setIsShooting(false);
    }
  }, [view.userId, view.projectId, view.viewId]);

  // Get DSLR status from heartbeat
  const dslrStatus = view.androidStatus?.device?.dslr;
  const isDslrConnected = dslrStatus?.connected ?? false;

  const handleDslrRefresh = useCallback(() => {
    setDslrRefreshing(true);
    updateDslrDesiredProperty(view.userId, view.projectId, view.viewId, 'refreshProperties', true)
      .catch((err) => console.error('[CameraTab] refresh request failed', err));
    setTimeout(() => setDslrRefreshing(false), 3000);
  }, [view.userId, view.projectId, view.viewId]);

  // Auto-refresh on mount if DSLR connected but capabilities not yet loaded
  const autoRefreshDone = useRef(false);
  useEffect(() => {
    if (isDslrConnected && !dslrStatus?.propertyCapabilities && !autoRefreshDone.current) {
      autoRefreshDone.current = true;
      handleDslrRefresh();
    }
  }, [isDslrConnected, dslrStatus?.propertyCapabilities, handleDslrRefresh]);

  // No device connected warning
  if (cameras.length === 0 && !isDslrConnected) {
    return (
      <Alert
        type="warning"
        title="No Device Connected"
        description={`Camera settings will be available once a device is paired to this view. Access your device and enter the token #${view.token}.`}
        showIcon
      />
    );
  }

  // DSLR Mode - Show DSLR settings instead of Android camera settings
  if (isDslrMode) {
    return (
      <div className="flex flex-col gap-4">
        <Form form={form} layout="vertical">
          {/* Camera Status */}
          {isDslrConnected && dslrStatus ? (
            <div
              style={{
                padding: 16,
                border: BORDER,
                borderRadius: RADIUS,
                background: SURFACE,
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <CameraImage
                modelName={dslrStatus.model}
                manufacturer={dslrStatus.manufacturer}
                variant="hero"
              />
              <div style={{ textAlign: 'center' }}>
                <FieldLabel>Connected camera</FieldLabel>
                <Text strong style={{ fontSize: 15, color: TEXT_STRONG }}>
                  {dslrStatus.manufacturer} {dslrStatus.model}
                </Text>
              </div>
            </div>
          ) : (
            <Alert
              type="warning"
              message="External Camera Disconnected"
              description="Camera is not connected. Connect an external camera via USB or switch to Internal mode."
              style={{ marginBottom: 16 }}
            />
          )}

          {/* DSLR Remote Controls */}
          {isDslrConnected && (
            <>
              {dslrStatus?.propertyCapabilities ? (
                <DslrControls
                  view={view}
                  userId={view.userId}
                  projectId={view.projectId}
                  viewId={view.viewId}
                  capabilities={dslrStatus.propertyCapabilities}
                  connected={isDslrConnected}
                  shootingMode={dslrStatus.cameraParameters?.shootingMode}
                  focusMode={dslrStatus.cameraParameters?.focusMode}
                  onRefresh={handleDslrRefresh}
                  refreshing={dslrRefreshing}
                />
              ) : dslrStatus?.controlsUnsupported ? (
                <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '12px 16px', border: '1px solid #d6e4ff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 20, color: '#1677ff' }}>info</span>
                    <Text type="secondary">{dslrStatus.controlsUnsupported}</Text>
                  </div>
                </div>
              ) : (
                <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text type="secondary">Loading camera parameters...</Text>
                    <Button
                      size="small"
                      icon={<span className="material-symbols-rounded" style={{ fontSize: 18, position: 'relative', top: 3 }}>refresh</span>}
                      loading={dslrRefreshing}
                      onClick={handleDslrRefresh}
                    />
                  </div>
                </div>
              )}
            </>
          )}


          {/* Test Shot Button - hide if parent will render it */}
          {!hideTestShotButton && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                size="large"
                className="btn-primary-emphasis"
                icon={<MaterialIcon name="photo_camera" size={18} />}
                loading={isShooting}
                onClick={handleTestShot}
              >
                {isShooting ? 'Shooting...' : 'Test shot'}
              </Button>
            </div>
          )}
        </Form>
      </div>
    );
  }

  // Android Camera - Show standard camera settings
  return (
    <div className="flex flex-col gap-4">
      <Form form={form} layout="vertical">
        {/* Section: Optics */}
        <div style={{ marginTop: SECTION_GAP }}>
          <SectionLabel>Optics</SectionLabel>
        </div>

        {/* Lens Selection (only show if multiple cameras) */}
        {hasMultipleCameras && (
          <div style={{ marginBottom: 20 }}>
            <FieldLabel>Available lens</FieldLabel>
            <Segmented
              className="segmented-emphasis"
              value={lens}
              onChange={(val) => {
                const newLens = val as CameraSettings['lens'];
                setLens(newLens);
                saveSettings({ lens: newLens });
              }}
              options={cameras.map(camera => ({
                label: camera.lens === 'wide' ? 'Wide'
                  : camera.lens === 'normal' ? 'Normal'
                  : 'Tele',
                value: camera.lens,
              }))}
            />
          </div>
        )}

        {/* Camera Info — card with label/value grid */}
        {selectedCamera && (
          <div
            style={{
              padding: 18,
              border: BORDER,
              borderRadius: RADIUS,
              background: SURFACE,
              marginBottom: 28,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: 18,
              columnGap: 24,
            }}
          >
            <OpticsStat icon="lens_blur" label="Sensor" value={`${selectedCamera.megapixels.toFixed(1)} MP`} />
            <OpticsStat
              icon="aspect_ratio"
              label="Max resolution"
              value={`${selectedCamera.maxResolution.width} × ${selectedCamera.maxResolution.height}`}
            />
            <OpticsStat icon="straighten" label="Focal length" value={`${selectedCamera.focalLength.toFixed(1)} mm`} />
            <OpticsStat icon="camera" label="Aperture" value={`f/${selectedCamera.aperture.toFixed(1)}`} />
            <OpticsStat
              icon="iso"
              label="ISO range"
              value={`${selectedCamera.isoRange.min} – ${selectedCamera.isoRange.max}`}
            />
            <OpticsStat
              icon={supportsRaw ? 'raw_on' : 'raw_off'}
              label="RAW"
              value={supportsRaw ? 'Available' : 'Not available'}
              valueColor={supportsRaw ? '#52c41a' : SECONDARY}
            />
          </div>
        )}

        {/* Focus Mode */}
        <div style={{ marginBottom: 28 }}>
          <FieldLabel>Focus mode</FieldLabel>
          <Segmented
            className="segmented-emphasis"
            value={focusMode}
            onChange={(val) => {
              const newFocusMode = val as CameraSettings['focusMode'];
              setFocusMode(newFocusMode);
              let newFocusDistance = manualFocusDistance;
              if (newFocusMode === 'manual') {
                newFocusDistance = 0;
                setManualFocusDistance(0);
              }
              saveSettings({ focusMode: newFocusMode, manualFocusDistance: newFocusDistance });
            }}
            options={[
              { label: 'Auto', value: 'auto' },
              { label: 'Manual', value: 'manual' },
            ]}
          />
          {focusMode === 'auto' && <InlineWarning text="Not recommended — focus may vary causing flicker" />}
        </div>

        {/* Focus Distance Slider (only when Manual) */}
        {focusMode === 'manual' && (
          <div style={{ marginBottom: 28 }}>
            <FieldLabel>Focus distance — {formatFocusDistance(manualFocusDistance)}</FieldLabel>
            <Slider
              className="slider-secondary"
              min={0}
              max={100}
              step={1}
              value={focusDistanceToPercent(manualFocusDistance, minFocusDistance)}
              onChange={(percent) => {
                const newDistance = percentToFocusDistance(percent, minFocusDistance);
                setManualFocusDistance(newDistance);
                debouncedSave({ manualFocusDistance: newDistance });
              }}
              disabled={minFocusDistance <= 0}
              tooltip={{ open: false }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <HelperText style={{ display: 'inline', marginTop: 0 }}>
                {formatMinDistance(minFocusDistance)}
              </HelperText>
              <HelperText style={{ display: 'inline', marginTop: 0 }}>∞</HelperText>
            </div>
            {minFocusDistance <= 0 && <InlineWarning text="This lens only supports fixed focus (infinity)" />}
          </div>
        )}

        <div style={{ marginTop: SECTION_GAP }}>
          <SectionLabel>Exposure</SectionLabel>
        </div>

        {/* HDR Mode */}
        {supportsHdr && (
          <div style={{ marginBottom: 28 }}>
            <FieldLabel>HDR (exposure bracketing)</FieldLabel>
            <Segmented
              className="segmented-emphasis"
              value={hdrMode}
              onChange={(val) => {
                const newHdr = val as CameraSettings['hdrMode'];
                setHdrMode(newHdr);
                if (newHdr !== 'off' && lnrMode === 'fusion') {
                  setLnrMode('classic');
                  saveSettings({ hdrMode: newHdr, lnrMode: 'classic' });
                } else {
                  saveSettings({ hdrMode: newHdr });
                }
              }}
              options={[
                { label: 'Off', value: 'off' },
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'Strong', value: 'strong' },
              ]}
            />
            <HelperText>
              {hdrMode === 'off' ? 'Single exposure' :
               hdrMode === 'low' ? '±1 EV bracket' :
               hdrMode === 'medium' ? '±2 EV bracket' : '±3 EV bracket'}
            </HelperText>
          </div>
        )}

        {/* LNR Mode (Low Noise Reduction) */}
        {supportsHdr && (
          <div style={{ marginBottom: 28 }}>
            <FieldLabel>Low noise reduction (long exposure / shadow fusion)</FieldLabel>
            <Segmented
              className="segmented-emphasis"
              value={lnrMode}
              onChange={(val) => {
                const newLnr = val as NonNullable<CameraSettings['lnrMode']>;
                setLnrMode(newLnr);
                if (newLnr === 'fusion' && hdrMode !== 'off') {
                  setHdrMode('off');
                  saveSettings({ lnrMode: newLnr, hdrMode: 'off' });
                } else {
                  saveSettings({ lnrMode: newLnr });
                }
              }}
              options={[
                { label: 'Off', value: 'off' },
                { label: 'Classic', value: 'classic' },
                { label: 'Fusion', value: 'fusion', disabled: isHdrOn },
              ]}
            />
            <HelperText>
              {lnrMode === 'off' ? 'Standard exposure' :
               lnrMode === 'classic' ? 'Low ISO + long shutter' :
               'Shadow noise reduction (2 frames)'}
            </HelperText>
          </div>
        )}

        {supportsHdr && viewId && (
          <div style={{ marginBottom: 28 }}>
            <Button
              block
              onClick={() => navigate(`/test-suite/${viewId}`)}
              icon={<MaterialIcon name="filter" size={18} />}
            >
              Explore Variations
            </Button>
          </div>
        )}

        {/* EV Compensation */}
        <div style={{ marginBottom: 28 }}>
          <FieldLabel>
            Exposure compensation — {(() => {
              const rounded = Math.round(evCompensation * 10) / 10;
              const text = rounded === 0 ? '0' : rounded.toFixed(1);
              return rounded > 0 ? `+${text}` : text;
            })()} EV
          </FieldLabel>
          <Slider
            className="slider-emphasis"
            min={evRange.min}
            max={evRange.max}
            step={evRange.step}
            marks={EV_MARKS}
            included={false}
            value={evCompensation}
            onChange={(val) => {
              setEvCompensation(val);
              debouncedSave({ evCompensation: val });
            }}
            tooltip={{ open: false }}
          />
        </div>

        <div style={{ marginTop: 32 }}>
          <SectionLabel>Output</SectionLabel>
        </div>

        <div
          style={{
            padding: 18,
            border: BORDER,
            borderRadius: RADIUS,
            background: SURFACE,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* JPEG Quality */}
          <div>
            <FieldLabel>JPEG quality</FieldLabel>
            <Select
              value={jpegQuality}
              onChange={(val) => {
                setJpegQuality(val);
                saveSettings({ jpegQuality: val });
              }}
              style={{ width: 220 }}
            >
              {JPEG_QUALITY_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* RAW Toggle (only show if supported) */}
          {supportsRaw && (
            <div>
              <FieldLabel>RAW capture (DNG)</FieldLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <GatedControl locked={!rawAllowed} tier="pro" feature="RAW capture">
                <Switch
                  checked={rawEnabled && rawAllowed}
                  disabled={!rawAllowed}
                  onChange={(val) => {
                    if (val) {
                      // No configuration means there is no interval to raise; the
                      // timer tab is where an unconfigured view gets one.
                      const currentInterval = view.timerSettings?.interval;
                      if (currentInterval !== undefined && currentInterval < 30) {
                        Modal.confirm({
                          title: 'RAW capture requires a minimum interval of 30 seconds',
                          content: `The current interval (${currentInterval}s) will be automatically raised to 30s.`,
                          okText: 'Enable RAW',
                          onOk: async () => {
                            setRawEnabled(true);
                            saveSettings({ rawEnabled: true });
                            const timerSettings = { ...view.timerSettings!, interval: 30 };
                            const capturing = view.capturing ?? false;
                            await updateTimerSettings(view.userId, view.projectId, view.viewId, capturing, timerSettings);
                            message.success('RAW enabled — interval raised to 30s');
                          },
                        });
                        return;
                      }
                    }
                    setRawEnabled(val);
                    saveSettings({ rawEnabled: val });
                  }}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
                </GatedControl>
                <HelperText style={{ display: 'inline', marginTop: 0 }}>
                  {isHdrOn ? 'Saves RAW of 0 EV frame' : 'Save DNG alongside JPEG'}
                </HelperText>
              </div>
            </div>
          )}
        </div>

        {/* Test Shot Button - hide if parent will render it */}
        {!hideTestShotButton && (
          <div style={{ marginTop: SECTION_GAP, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              size="large"
              className="btn-primary-emphasis"
              icon={<MaterialIcon name="photo_camera" size={18} />}
              loading={isShooting}
              onClick={handleTestShot}
            >
              {isShooting ? 'Shooting...' : 'Test shot'}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
}

/** Single stat row used in the Optics card. */
function OpticsStat({
  icon,
  label,
  value,
  valueColor = TEXT_STRONG,
}: {
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <MaterialIcon name={icon} size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
        <span style={{ fontSize: 11, color: SECONDARY, letterSpacing: 0.3, textTransform: 'uppercase', fontWeight: 500 }}>
          {label}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </span>
      </div>
    </div>
  );
}

/** Inline warning shown below a control. Soft amber, small icon, scoped. */
function InlineWarning({ text }: { text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 6,
        color: WARNING_SOFT,
        fontSize: 12,
      }}
    >
      <MaterialIcon name="warning" size={14} />
      <span>{text}</span>
    </div>
  );
}
